import React from 'react';
import { Box, Typography, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Chip } from '@mui/material';

const SchemaReview = () => {
  const appointmentFields = [
    { name: 'id', type: 'String', required: 'Yes', description: 'Unique identifier' },
    { name: 'patientId', type: 'String', required: 'Yes', description: 'Reference to Patient' },
    { name: 'doctorId', type: 'String', required: 'Yes', description: 'Reference to Employee (Doctor)' },
    { name: 'date', type: 'DateTime', required: 'Yes', description: 'Appointment date' },
    { name: 'startTime', type: 'String', required: 'Yes', description: 'Appointment start time (HH:MM)' },
    { name: 'endTime', type: 'String', required: 'Yes', description: 'Appointment end time (HH:MM)' },
    { name: 'status', type: 'String', required: 'No', description: 'scheduled, completed, cancelled, no_show, in_progress' },
    { name: 'notes', type: 'String', required: 'No', description: 'Additional notes' },
    { name: 'appointmentType', type: 'String', required: 'No', description: 'consultation, pharmacy, procedure, etc.' },
  ];

  const orderFields = [
    { name: 'id', type: 'String', required: 'Yes', description: 'Unique identifier' },
    { name: 'appointmentId', type: 'String', required: 'Yes', description: 'Reference to Appointment' },
    { name: 'itemId', type: 'String', required: 'No', description: 'Reference to Item (optional)' },
    { name: 'itemName', type: 'String', required: 'Yes', description: 'Denormalized item name' },
    { name: 'currentPrice', type: 'Float', required: 'Yes', description: 'Price at time of order' },
    { name: 'variedPrice', type: 'Float', required: 'No', description: 'Adjusted price if different' },
    { name: 'quantity', type: 'Int', required: 'Yes', description: 'Number of items' },
    { name: 'discountAmount', type: 'Float', required: 'Yes', description: 'Discount applied' },
    { name: 'total', type: 'Float', required: 'Yes', description: 'Final amount' },
    { name: 'procedureStatus', type: 'String', required: 'No', description: 'pending, in_progress, completed, cancelled' },
    { name: 'financialStatus', type: 'String', required: 'No', description: 'pending, partially_paid, paid, refunded' },
    { name: 'isDispensed', type: 'Boolean', required: 'Yes', description: 'If pharmacy item was dispensed' },
  ];

  const sampleAppointment = {
    id: '550e8400-e29b-41d4-a716-446655440000',
    patientId: 'patient-123',
    doctorId: 'doctor-456',
    date: '2023-12-15T00:00:00.000Z',
    startTime: '14:00',
    endTime: '14:30',
    status: 'scheduled',
    appointmentType: 'consultation',
    notes: 'Follow-up visit',
  };

  const sampleOrder = {
    id: 'order-123',
    appointmentId: '550e8400-e29b-41d4-a716-446655440000',
    itemId: 'item-789',
    itemName: 'Paracetamol 500mg',
    currentPrice: 5.99,
    quantity: 2,
    discountAmount: 0,
    total: 11.98,
    procedureStatus: 'pending',
    financialStatus: 'pending',
    isDispensed: false,
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Appointment & Order Schema Review
      </Typography>
      
      <Typography variant="h6" mt={4} mb={2}>
        Appointment Model
      </Typography>
      <TableContainer component={Paper} sx={{ mb: 4 }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Field</TableCell>
              <TableCell>Type</TableCell>
              <TableCell>Required</TableCell>
              <TableCell>Description</TableCell>
              <TableCell>Sample Value</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {appointmentFields.map((field) => (
              <TableRow key={field.name}>
                <TableCell>{field.name}</TableCell>
                <TableCell>{field.type}</TableCell>
                <TableCell>{field.required}</TableCell>
                <TableCell>{field.description}</TableCell>
                <TableCell>
                  <Chip 
                    label={JSON.stringify(sampleAppointment[field.name as keyof typeof sampleAppointment] || 'null')} 
                    size="small" 
                    variant="outlined"
                  />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Typography variant="h6" mt={4} mb={2}>
        AppointmentOrder Model
      </Typography>
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Field</TableCell>
              <TableCell>Type</TableCell>
              <TableCell>Required</TableCell>
              <TableCell>Description</TableCell>
              <TableCell>Sample Value</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {orderFields.map((field) => (
              <TableRow key={field.name}>
                <TableCell>{field.name}</TableCell>
                <TableCell>{field.type}</TableCell>
                <TableCell>{field.required}</TableCell>
                <TableCell>{field.description}</TableCell>
                <TableCell>
                  <Chip 
                    label={JSON.stringify(sampleOrder[field.name as keyof typeof sampleOrder] ?? 'null')} 
                    size="small" 
                    variant="outlined"
                  />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Box mt={4} p={3} bgcolor="#f5f5f5" borderRadius={1}>
        <Typography variant="h6" gutterBottom>
          Key Points to Review:
        </Typography>
        <ul>
          <li>Appointment has a one-to-many relationship with AppointmentOrder</li>
          <li>AppointmentOrder has an optional relationship with Item (for inventory items)</li>
          <li>All models include audit fields (createdAt, updatedAt, createdBy, updatedBy)</li>
          <li>Item name is denormalized in the order for historical reference</li>
          <li>Prices are stored at time of order to maintain history</li>
        </ul>
      </Box>
    </Box>
  );
};

export default SchemaReview;
